import { isNessusScanFinding } from './findingClassification';

const SEV_RANK = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };

// Parse additionalData from detail (may be JSON string) or top-level
export function parseAdditionalData(f) {
  if (f?.additionalData && typeof f.additionalData === 'object') return f.additionalData;
  if (f?.AdditionalData && typeof f.AdditionalData === 'object') return f.AdditionalData;
  const raw = f?.detail?.additionalData ?? f?.detail?.AdditionalData ?? f?.additionalData ?? f?.AdditionalData;
  if (!raw) return null;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return typeof raw === 'object' ? raw : null;
}

export function sourceAppOf(f) {
  // additionalData.sourceApp first — detail.sourceApp is client profile name, not target app
  const ad = parseAdditionalData(f);
  if (ad?.sourceApp) return ad.sourceApp;
  const app = f?.detail?.sourceDomain;
  if (app) return app;
  const candidate = f?.detail?.affectedComponent ?? f?.asset ?? '';
  try {
    if (/^https?:\/\//i.test(candidate)) return new URL(candidate).hostname;
  } catch { /* ignore */ }
  const hostMatch = candidate.match(/^([a-z0-9.-]+\.[a-z]{2,})/i);
  return hostMatch ? hostMatch[1] : (candidate || '');
}

export function methodOf(f) {
  const ad = parseAdditionalData(f);
  const m = ad?.method ?? f?.detail?.method;
  return m ? String(m).toUpperCase() : '';
}

export function pathOf(f) {
  const loc = typeof f === 'string' ? f : locationOf(f);
  try {
    if (/^https?:\/\//i.test(loc)) return new URL(loc).pathname + (new URL(loc).search || '');
  } catch { /* ignore */ }
  return loc;
}

export function cweIdOf(f) {
  const ad = parseAdditionalData(f);
  const id = ad?.cweid ?? ad?.cweId ?? ad?.CweId ?? f?.detail?.cweid ?? '';
  return id ? String(id) : '';
}

// Human-readable per-finding location: URL, host:port, IP, or file path.
// Used when rendering expanded rows so each instance shows where it was found.
export function locationOf(f) {
  const d = f?.detail ?? {};
  const ad = parseAdditionalData(f) ?? {};
  return d.affectedComponent
    ?? d.url
    ?? d.uri
    ?? d.target
    ?? d.endpoint
    ?? ad.affectedComponent
    ?? ad.url
    ?? ad.uri
    ?? f?.asset
    ?? d.sourceApp
    ?? d.sourceDomain
    ?? '';
}

function uniq(values) {
  const seen = new Set();
  const out = [];
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (!text) continue;
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(text);
  }
  return out;
}

function asArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch { /* fall through */ }
    }
    return trimmed.split(/[;\n]/).map(x => x.trim()).filter(Boolean);
  }
  return [value];
}

function firstDefined(...values) {
  return values.find(value => value !== undefined && value !== null && value !== '');
}

export function duplicateGroupKeyOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  const value = firstDefined(
    f?.duplicateGroupKey,
    f?.DuplicateGroupKey,
    f?.detail?.duplicateGroupKey,
    f?.detail?.DuplicateGroupKey,
    ad.duplicateGroupKey,
    ad.DuplicateGroupKey
  );
  return value ? String(value) : '';
}

export function duplicateGroupCountOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  const raw = firstDefined(
    f?.duplicateGroupCount,
    f?.DuplicateGroupCount,
    f?.detail?.duplicateGroupCount,
    f?.detail?.DuplicateGroupCount,
    ad.duplicateGroupCount,
    ad.DuplicateGroupCount
  );
  const count = Number(raw);
  return Number.isFinite(count) && count > 1 ? Math.floor(count) : 0;
}

const IP_RE = /\b(?:25[0-5]|2[0-4]\d|1?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|1?\d?\d)){3}\b/;
const DOMAIN_RE = /\b(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}\b/i;

function cveOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  return firstDefined(f?.cve, f?.cveId, f?.detail?.cve, f?.detail?.cveId, ad.cve, ad.cveId, ad.CveId);
}

function dataTypeOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  return firstDefined(
    sddCategory(f),
    ad.dataType,
    ad.DataType,
    ad.category,
    ad.patternCategory,
    ad.pattern_category,
    ad.credentialType,
    f?.detail?.dataType,
    f?.detail?.category
  );
}

function domainOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  const candidates = [
    ad.domain,
    ad.sourceDomain,
    ad.hostname,
    ad.hostName,
    f?.detail?.sourceDomain,
    f?.detail?.host,
    f?.detail?.hostname,
    sourceAppOf(f),
    f?.asset,
    locationOf(f),
  ];
  for (const candidate of candidates) {
    const text = String(candidate ?? '').trim();
    if (!text || IP_RE.test(text)) continue;
    try {
      if (/^https?:\/\//i.test(text)) return new URL(text).hostname;
    } catch { /* ignore */ }
    const match = text.match(DOMAIN_RE);
    if (match) return match[0].toLowerCase();
  }
  return '';
}

function ipOf(f) {
  const ad = parseAdditionalData(f) ?? {};
  const candidates = [
    ad.ip,
    ad.ipAddress,
    ad.hostIp,
    f?.detail?.ip,
    f?.detail?.ipAddress,
    f?.asset,
    locationOf(f),
  ];
  for (const candidate of candidates) {
    const match = String(candidate ?? '').match(IP_RE);
    if (match) return match[0];
  }
  return '';
}

function readableDuplicateKey(key) {
  const raw = String(key ?? '').trim();
  if (!raw) return '';
  const parts = raw.split(':').map(x => x.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const prefix = parts[0].toLowerCase();
    const value = parts.slice(1).join(':');
    if (['cve', 'data-type', 'datatype', 'data', 'domain', 'ip'].includes(prefix)) return value;
  }
  return raw;
}

export function duplicateGroupDisplayForGroup(group) {
  const findings = Array.isArray(group?.findings) ? group.findings : [];
  if (findings.length <= 1) return null;

  const first = findings[0] ?? group?.first ?? {};
  const key = duplicateGroupKeyOf(first);
  const cves = uniq(findings.map(cveOf));
  const dataTypes = uniq(findings.map(dataTypeOf).map(formatCategory));
  const domains = uniq(findings.map(domainOf));
  const ips = uniq(findings.map(ipOf));

  if (cves.length > 0) return { label: `Grouped by CVE: ${cves.slice(0, 2).join(', ')}${cves.length > 2 ? ` +${cves.length - 2}` : ''}`, kind: 'cve' };
  if (dataTypes.length > 0) return { label: `Grouped by data type: ${dataTypes.slice(0, 2).join(', ')}${dataTypes.length > 2 ? ` +${dataTypes.length - 2}` : ''}`, kind: 'data-type' };
  if (domains.length > 0) return { label: `Grouped by domain: ${domains.slice(0, 2).join(', ')}${domains.length > 2 ? ` +${domains.length - 2}` : ''}`, kind: 'domain' };
  if (ips.length > 0) return { label: `Grouped by IP: ${ips.slice(0, 2).join(', ')}${ips.length > 2 ? ` +${ips.length - 2}` : ''}`, kind: 'ip' };
  if (key) return { label: `Grouped by duplicate key: ${readableDuplicateKey(key)}`, kind: 'duplicate-key' };
  return { label: 'Grouped by similar finding title', kind: 'title' };
}

export function isDuplicateCandidateFinding(f) {
  const ad = parseAdditionalData(f) ?? {};
  const value = firstDefined(
    f?.isDuplicateCandidate,
    f?.IsDuplicateCandidate,
    f?.detail?.isDuplicateCandidate,
    f?.detail?.IsDuplicateCandidate,
    ad.isDuplicateCandidate,
    ad.IsDuplicateCandidate
  );
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return Boolean(value);
}

export function duplicateSummaryFor(findings) {
  const groups = new Map();
  let candidateCount = 0;
  for (const f of findings) {
    if (!isDuplicateCandidateFinding(f)) continue;
    candidateCount += 1;
    const key = duplicateGroupKeyOf(f);
    if (!key) continue;
    const count = duplicateGroupCountOf(f);
    groups.set(key, Math.max(groups.get(key) ?? 0, count || 1));
  }
  return {
    candidateCount,
    groupCount: groups.size,
    groupedFindingCount: [...groups.values()].reduce((sum, count) => sum + count, 0),
  };
}

export function affectedLocations(f) {
  const d = f?.detail ?? {};
  const ad = parseAdditionalData(f) ?? {};
  const sddPath = sddResourcePath(f);
  const urlish = [
    d.resourcePath,
    d.url,
    d.uri,
    d.target,
    d.endpoint,
    ad.resourcePath,
    ad.ResourcePath,
    ad.filePath,
    ad.FilePath,
    ad.path,
    ad.objectKey,
    ad.key,
    ad.webUrl,
    ad.webURL,
    ad.fileUrl,
    ad.objectUrl,
    ad.url,
    ad.uri,
    sddPath,
  ];
  const hostish = [
    f?.asset,
    d.affectedComponent,
    d.host,
    d.hostname,
    d.ip,
    d.ipAddress,
    d.sourceApp,
    d.sourceDomain,
    ad.affectedComponent,
    ad.host,
    ad.hostname,
    ad.hostName,
    ad.ip,
    ad.ipAddress,
    ad.hostIp,
    ad.fqdn,
    ad.asset,
    ad.sourceApp,
    ad.domain,
  ];

  const paths = uniq([
    ...asArray(d.affectedPaths),
    ...asArray(ad.affectedPaths),
    ...urlish,
  ]);
  const pathKeys = new Set(paths.map(x => x.toLowerCase()));
  const hosts = uniq([
    ...asArray(d.affectedHosts),
    ...asArray(ad.affectedHosts),
    ...hostish,
  ]).filter(host => !pathKeys.has(host.toLowerCase()));
  return { paths, hosts, total: paths.length + hosts.length };
}

// Returns empty string when nothing usable — callers should render nothing,
// not an em-dash, per UX spec.
export function targetLabel(f, groupCount = 1) {
  const app = sourceAppOf(f);
  const { total: pathTotal } = affectedLocations(f);
  const count = Math.max(groupCount, pathTotal || 0);
  if (!app) return '';
  if (count > 1) return `${app} (${count})`;
  return app;
}

export function highestSev(group) {
  let best = null;
  let bestRank = -1;
  for (const f of group) {
    const r = SEV_RANK[f.sev] ?? -1;
    if (r > bestRank) { bestRank = r; best = f.sev; }
  }
  return best ?? 'info';
}

function normalizedFindingText(f) {
  return [
    f?.title,
    f?.description,
    f?.desc,
    f?.remediation,
    f?.rem,
    f?.module,
    f?.fingerprint,
    f?.asset,
    f?.detail?.affectedComponent,
    f?.detail?.sourceDomain,
  ].filter(Boolean).join(' ').toLowerCase();
}

function rollupTitleForFinding(f) {
  const text = normalizedFindingText(f);
  const sev = String(f?.sev ?? f?.severity ?? '').toLowerCase();
  const originalTitle = (f?.title || '').trim() || f?.id || 'Untitled finding';

  // Nessus plugin names are the technical finding identity. Generic executive
  // rollups hide distinct plugins, CVEs, severities, and remediation context.
  if (isNessusScanFinding(f)) return originalTitle;

  if (/\b(deprecated|weak|legacy|old)\b.*\b(tls|ssl)\b|\b(tls|ssl)\b.*\b(1\.0|1\.1|deprecated|weak|legacy|old)\b/.test(text)) {
    return 'Deprecated TLS/SSL';
  }
  if (/\b(?:cert|certificate|ssl certificate|tls certificate)\b.*\b(?:expir\w*|renew\w*)\b|\bexpir\w*\b.*\b(?:cert|certificate)\b/.test(text)) {
    return 'Certificate Expiring Soon';
  }
  if (/\b(exposed|open|public|internet[-\s]?facing)\b.*\b(service|port|ssh|rdp|ftp|smtp|mysql|postgres|mssql|redis|elasticsearch|kibana|mongodb)\b|\b(service|port)\b.*\b(exposed|open|public|internet[-\s]?facing)\b/.test(text)) {
    return 'Exposed Services';
  }
  if (/\bdmarc\b.*\b(not enforced|none|monitor|quarantine not|reject not|p=none|policy)\b|\bnot enforced\b.*\bdmarc\b/.test(text)) {
    return 'DMARC Not Enforced';
  }
  if (sev === 'info' || sev === 'informational' || /\binformational\b/.test(text)) {
    return 'Informational Items';
  }
  return originalTitle;
}

export function groupByTitle(findings) {
  const map = new Map();
  for (const f of findings) {
    const title = rollupTitleForFinding(f);
    const duplicateGroupKey = duplicateGroupKeyOf(f).trim().toLowerCase();
    const key = duplicateGroupKey ? `duplicate:${duplicateGroupKey}` : `title:${title.toLowerCase()}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(f);
  }
  const groups = [];
  for (const [key, arr] of map.entries()) {
    const title = rollupTitleForFinding(arr[0]);
    const first = { ...arr[0], title };
    groups.push({
      key,
      title,
      first,
      findings: arr,
      count: arr.length,
      sev: highestSev(arr),
      sourceApp: sourceAppOf(first),
    });
  }
  return groups;
}

// --- SDD helpers ---

function formatCategory(cat) {
  if (!cat) return '';
  return cat.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// Parse SDD AWS finding fields from additionalData first, fingerprint fallback.
// Fingerprint format: sdd-aws:{bucket}:{key}:{category}
export function parseSddAws(f) {
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const ad = parseAdditionalData(f);
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const sourceType = (ad?.sourceType ?? ad?.source_type ?? '').toLowerCase();
  if (!fp.startsWith('sdd-aws:') && !mod.startsWith('sdd-aws') && !sourceType.includes('s3')) return null;
  const parts = fp.split(':');
  const bucket = ad?.bucket ?? ad?.bucketName ?? parts[1] ?? '';
  const key = ad?.key ?? ad?.objectKey ?? parts[2] ?? '';
  const category = ad?.category ?? ad?.patternCategory ?? parts[3] ?? '';
  const lineNumber = ad?.lineNumber ?? ad?.line ?? null;
  const innerPath = ad?.innerPath ?? ad?.innerXmlPath ?? null;
  const resourcePath = ad?.resourcePath ?? f?.detail?.resourcePath ?? (bucket ? `s3://${bucket}${key ? `/${key}` : ''}` : '');
  return { bucket, key, category, lineNumber, innerPath, resourcePath, source: 'S3' };
}

// Parse SDD Cloud finding fields from additionalData first, fingerprint fallback.
// Fingerprint format: sdd-cloud:{driveId}:{filePath}:{category}
export function parseSddCloud(f) {
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const ad = parseAdditionalData(f);
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const sourceTypeForMatch = (ad?.sourceType ?? ad?.source_type ?? '').toLowerCase();
  const isCloudSdd = fp.startsWith('sdd-cloud:')
    || mod.startsWith('sdd-cloud')
    || sourceTypeForMatch.includes('sharepoint')
    || sourceTypeForMatch.includes('onedrive')
    || sourceTypeForMatch.includes('azure-blob')
    || sourceTypeForMatch.includes('azureblob')
    || sourceTypeForMatch === 'blob';
  if (!isCloudSdd) return null;
  const parts = fp.split(':');
  const siteUrl = ad?.siteUrl ?? ad?.site_url ?? ad?.site ?? '';
  const siteName = ad?.siteName ?? ad?.site_name ?? '';
  const driveName = ad?.drive ?? ad?.driveName ?? ad?.drive_name ?? parts[1] ?? '';
  const driveId = ad?.driveId ?? ad?.drive_id ?? parts[1] ?? '';
  const filePath = ad?.filePath ?? ad?.file_path ?? ad?.path ?? (parts.length > 3 ? parts.slice(2, -1).join(':') : parts[2] ?? '');
  const category = ad?.category ?? ad?.patternCategory ?? ad?.pattern_category ?? (parts.length > 1 ? parts[parts.length - 1] : '');
  const sourceType = sourceTypeForMatch;
  const accountName = ad?.accountName ?? ad?.account_name ?? '';
  const containerName = ad?.containerName ?? ad?.container_name ?? '';
  const blobName = ad?.blobName ?? ad?.blob_name ?? '';
  const resourcePath = ad?.resourcePath ?? f?.detail?.resourcePath ?? f?.detail?.affectedComponent ?? '';
  const source = sourceType.includes('onedrive') ? 'OneDrive'
    : sourceType.includes('sharepoint') ? 'SharePoint'
    : (sourceType.includes('azure-blob') || sourceType.includes('azureblob') || sourceType.includes('blob')) ? 'Azure Blob'
    : siteName ? 'SharePoint'
    : driveName ? 'OneDrive'
    : 'Cloud';
  return { siteUrl, siteName, driveName, driveId, filePath, category, sourceType, source, accountName, containerName, blobName, resourcePath };
}

export function parseSddFileShare(f) {
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const ad = parseAdditionalData(f);
  const sourceType = (ad?.sourceType ?? ad?.source_type ?? '').toLowerCase();
  if (!fp.startsWith('sdd-fileshare:') && !sourceType.includes('file-share')) return null;
  const shareRoot = ad?.shareRoot ?? ad?.share_root ?? ad?.sharePath ?? ad?.share_path ?? '';
  const filePath = ad?.filePath ?? ad?.file_path ?? '';
  const category = ad?.category ?? ad?.patternCategory ?? ad?.pattern_category ?? '';
  const resourcePath = ad?.resourcePath ?? f?.detail?.resourcePath ?? f?.detail?.affectedComponent ?? '';
  return { shareRoot, filePath, category, resourcePath, source: 'File Share' };
}

// Unified SDD resource path for table display
export function sddResourcePath(f) {
  const aws = parseSddAws(f);
  if (aws) {
    const loc = aws.resourcePath || (aws.key ? `s3://${aws.bucket}/${aws.key}` : `s3://${aws.bucket}`);
    return aws.innerPath ? `${loc} (${aws.innerPath})` : loc;
  }
  const cloud = parseSddCloud(f);
  if (cloud) {
    if (cloud.resourcePath) return cloud.resourcePath;
    if (cloud.source === 'Azure Blob') {
      const base = cloud.accountName ? `https://${cloud.accountName}.blob.core.windows.net` : '';
      const path = [cloud.containerName, cloud.blobName].filter(Boolean).join('/');
      return base && path ? `${base}/${path}` : path || base;
    }
    const base = cloud.siteUrl || cloud.siteName || cloud.driveName || '';
    return cloud.filePath ? (base ? `${base}/${cloud.filePath}` : cloud.filePath) : base || cloud.driveId;
  }
  const fileShare = parseSddFileShare(f);
  if (fileShare) {
    return fileShare.resourcePath || (fileShare.shareRoot && fileShare.filePath ? `${fileShare.shareRoot}/${fileShare.filePath}` : fileShare.shareRoot || fileShare.filePath);
  }
  return f?.detail?.resourcePath || f?.asset || '';
}

// Pattern category from any SDD finding
export function sddCategory(f) {
  const aws = parseSddAws(f);
  if (aws) return aws.category;
  const cloud = parseSddCloud(f);
  if (cloud) return cloud.category;
  const fileShare = parseSddFileShare(f);
  if (fileShare) return fileShare.category;
  return '';
}

export function mergeAffectedLocations(group) {
  const paths = new Set();
  const hosts = new Set();
  for (const f of group) {
    const { paths: p, hosts: h } = affectedLocations(f);
    p.forEach(x => paths.add(x));
    h.forEach(x => hosts.add(x));
    if (f.asset) hosts.add(f.asset);
  }
  return { paths: [...paths], hosts: [...hosts] };
}
